/**
 * Capture the current frame with blue circle overlay
 * @param centerX - Circle center X in image space (0-450)
 * @param centerY - Circle center Y in image space (0-800)
 * @param radius - Circle radius in image space pixels
 */
export async function captureFrameWithCircle(
  centerX: number,
  centerY: number,
  radius: number
): Promise<Blob> {
  // Find the selected frame container, then get its image
  const frameContainer = document.querySelector('[data-selected-frame="true"]')
  if (!frameContainer) {
    throw new Error('Selected frame not found')
  }
  
  const imgElement = frameContainer.querySelector('img') as HTMLImageElement
  if (!imgElement) {
    throw new Error('Frame image not found in selected frame')
  }
  
  if (!imgElement.complete || !imgElement.naturalWidth) {
    throw new Error('Frame image not loaded')
  }

  // Image space is typically 450x800, but we'll use natural dimensions for accuracy
  const imageSpaceWidth = 450
  const imageSpaceHeight = 800

  // Create canvas at natural image size
  const canvas = document.createElement('canvas')
  canvas.width = imgElement.naturalWidth
  canvas.height = imgElement.naturalHeight
  const ctx = canvas.getContext('2d')!

  // Draw frame image
  ctx.drawImage(imgElement, 0, 0)

  // Scale coordinates from image space to natural dimensions
  const scaleX = imgElement.naturalWidth / imageSpaceWidth
  const scaleY = imgElement.naturalHeight / imageSpaceHeight
  const scaledX = centerX * scaleX
  const scaledY = centerY * scaleY
  const scaledRadius = radius * Math.min(scaleX, scaleY)

  // Draw blue circle
  ctx.strokeStyle = '#3b82f6'
  ctx.lineWidth = 4 * Math.min(scaleX, scaleY)
  ctx.beginPath()
  ctx.arc(scaledX, scaledY, scaledRadius, 0, Math.PI * 2)
  ctx.stroke()

  // Draw crosshair
  ctx.strokeStyle = '#3b82f6'
  ctx.lineWidth = 3 * Math.min(scaleX, scaleY)
  const crosshairSize = 15 * Math.min(scaleX, scaleY)
  ctx.beginPath()
  ctx.moveTo(scaledX - crosshairSize, scaledY)
  ctx.lineTo(scaledX + crosshairSize, scaledY)
  ctx.moveTo(scaledX, scaledY - crosshairSize)
  ctx.lineTo(scaledX, scaledY + crosshairSize)
  ctx.stroke()

  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('Failed to create image'))
        }
      },
      'image/jpeg',
      0.92
    )
  })
}

/**
 * Crop a square thumbnail around an item location on the current frame.
 * Returns a base64 data URL string (persistable in localStorage).
 * @param centerX - Circle center X in image space (0-450)
 * @param centerY - Circle center Y in image space (0-800)
 * @param radius - Circle radius in image space pixels
 * @param size - Output thumbnail size in pixels (default 128)
 */
export async function cropItemThumbnail(
  centerX: number,
  centerY: number,
  radius: number,
  size: number = 128
): Promise<string | null> {
  try {
    const frameContainer = document.querySelector('[data-selected-frame="true"]')
    if (!frameContainer) return null

    const imgElement = frameContainer.querySelector('img') as HTMLImageElement
    if (!imgElement || !imgElement.complete || !imgElement.naturalWidth) return null

    const imageSpaceWidth = 450
    const imageSpaceHeight = 800

    // Scale from image space to natural pixel dimensions
    const scaleX = imgElement.naturalWidth / imageSpaceWidth
    const scaleY = imgElement.naturalHeight / imageSpaceHeight
    const scale = Math.min(scaleX, scaleY)

    // Compute crop region in natural pixel coords
    const cropRadius = Math.max(radius * scale, 40 * scale) // minimum 40px in image space
    const scaledX = centerX * scaleX
    const scaledY = centerY * scaleY

    // Clamp to image bounds
    const sx = Math.max(0, Math.floor(scaledX - cropRadius))
    const sy = Math.max(0, Math.floor(scaledY - cropRadius))
    const sw = Math.min(Math.floor(cropRadius * 2), imgElement.naturalWidth - sx)
    const sh = Math.min(Math.floor(cropRadius * 2), imgElement.naturalHeight - sy)

    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!

    // Draw cropped region scaled to output size
    ctx.drawImage(imgElement, sx, sy, sw, sh, 0, 0, size, size)

    return canvas.toDataURL('image/jpeg', 0.85)
  } catch (err) {
    console.error('[cropItemThumbnail] Failed:', err)
    return null
  }
}
